/**
 * The on-site clock, as the screen reads it (F7, D86).
 *
 * Pure helpers so the arithmetic is testable with no React and no clock: the
 * component hands in "now". A running entry has no end yet and minutes 0 on
 * the row, so everything here that wants "time so far" adds the running
 * stretch itself — rounded to whole minutes the way `actions.stopClock` does
 * when it closes the entry, so the figure on screen is the figure that lands.
 */

export type ClockEntry = {
  user_id?: string | null;
  kind?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  minutes?: number | null;
  billable?: boolean | null;
};

/** 3723 s -> "1:02:03"; under an hour it is still "0:12:03", the way a stopwatch reads. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Milliseconds from `startedAt` to `now`; 0 for a missing or unreadable start, never negative. */
export function elapsedMs(startedAt: string | null | undefined, now: number): number {
  if (!startedAt) return 0;
  const start = Date.parse(startedAt);
  return Number.isNaN(start) ? 0 : Math.max(0, now - start);
}

/** Whole minutes a running entry has been going, rounded as the stop does. */
export function runningMinutes(entry: ClockEntry, now: number): number {
  return Math.round(elapsedMs(entry.started_at, now) / 60_000);
}

// A running clock is open, a clock kind, and has no minutes yet. Booked time
// with no end time is not a clock: taking it for one paused and "stopped" an
// hour somebody had booked (D86).
const isClockKind = (e: ClockEntry) => e.kind === "on_site" || e.kind === "break";
const isClock = (e: ClockEntry) => isClockKind(e) && !Number(e.minutes ?? 0);

/** The open on-site or break entry for this person, if any. */
export function runningFor(entries: ClockEntry[], userId: string): ClockEntry | null {
  return entries.find((e) => !e.ended_at && isClock(e) && e.user_id === userId) ?? null;
}

/** Minutes an entry counts for: its booked minutes, or the running stretch if it is still open. */
export function entryMinutes(entry: ClockEntry, now: number): number {
  return entry.ended_at || !isClock(entry) ? Number(entry.minutes ?? 0) : runningMinutes(entry, now);
}

/**
 * The entries as the service-call arithmetic should see them right now: a
 * running on-site entry carries its elapsed minutes, so the breakdown moves
 * while the tech is still on site. A break never counts (it is not billable).
 */
export function withRunningMinutes<T extends ClockEntry>(entries: T[], now: number): T[] {
  return entries.map((e) => (e.ended_at || e.kind !== "on_site" ? e : { ...e, minutes: runningMinutes(e, now) }));
}

/** Local calendar day of an ISO stamp, "YYYY-MM-DD" — the device runs on business time. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** On-site and break minutes started today (running stretch included). */
export function todayTotals(entries: ClockEntry[], now: number): { onSite: number; breaks: number } {
  const today = dayKey(new Date(now).toISOString());
  let onSite = 0;
  let breaks = 0;
  for (const e of entries) {
    if (!e.started_at || !isClockKind(e) || dayKey(e.started_at) !== today) continue;
    const minutes = entryMinutes(e, now);
    if (e.kind === "break") breaks += minutes;
    else onSite += minutes;
  }
  return { onSite, breaks };
}
